import { useMemo } from 'react';
import React from 'react';
import {
  Box,
  Field,
  FieldTypes,
  Inscription,
} from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import { NO_KEY, YES_KEY } from '@/common/constants';
import { useFieldsWithDefaultValues } from '@/utils';

import { useOrphanConfigurationStore } from '../stores';

import useEmployerOrganizations from './useEmployerOrganizations';
import useOrphanConfigurationFieldsPermProps from './useOrphanConfigurationFieldsPermProps';

const useOrphanConfigurationFields = () => {
  const {
    isOrganizationOrphanPermProps,
    orphanStartTimingDaysPermProps,
    orphanOrganizationIdPermProps,
  } = useOrphanConfigurationFieldsPermProps();
  const defaultValues = useOrphanConfigurationStore((state) =>state.sourceState); 
  const  {data: organizations}  = useEmployerOrganizations();
  const fields = useMemo<Field[]>(() => [
    ...(isOrganizationOrphanPermProps?.isAbsent ? [] : [
      {
        name: 'isEmployerOrphan',
        type: FieldTypes.Radio,
        label: 'Is this employer - orphan?',
        options: [
          {
            label: 'Yes',
            value: YES_KEY,
          },
          {
            label: 'No',
            value: NO_KEY,
          },
        ],
        disabled: isOrganizationOrphanPermProps?.disabled,
        subFields: [
          ...(orphanStartTimingDaysPermProps?.isAbsent ? [] : [
            {
              name: 'startOrphanProcessDays',
              type: FieldTypes.Number,
              label: (
                <Box flex={{ grow: 1 }}>
                  <Inscription margin={{ bottom: '4px' }} weight="bold" color="textBody">
                    Start orphan process (days)
                  </Inscription>
                  <Box>
                    <Inscription weight={500} color="textSecondary" size="small" lineHeight='18px'>
                      Enter number of days after employment termination for orphan process to begin
                    </Inscription>
                  </Box>
                </Box>
              ),
              showRequireIcon: typeof orphanStartTimingDaysPermProps?.showRequireIcon === 'boolean'
                ? orphanStartTimingDaysPermProps?.showRequireIcon
                : true,
              placeholder: 'Change date',
              parentValue: NO_KEY,
              validator: VALIDATORS.REQUIRED_POSITIVE_NUMBER_ONLY,
              disabled: orphanStartTimingDaysPermProps?.disabled,
            },
          ]),
          ...(orphanOrganizationIdPermProps?.isAbsent ? [] : [{
            name: 'orphanEmployer',
            type: FieldTypes.AutocompleteDropdown,
            label: 'Specify orphan employer',
            showRequireIcon: typeof orphanOrganizationIdPermProps?.showRequireIcon === 'boolean'
              ? orphanOrganizationIdPermProps?.showRequireIcon
              : true,
            placeholder: 'Select employer',
            parentValue: NO_KEY,
            options: organizations,
            validator: VALIDATORS.REQUIRED_STRING,
            disabled: orphanOrganizationIdPermProps?.disabled,
          } as Field]),
        ] as Field[],
      } as Field,
    ]),
  ], [
    organizations,
    isOrganizationOrphanPermProps,
    orphanStartTimingDaysPermProps,
    orphanOrganizationIdPermProps,
  ]);
  const formattedFields = useFieldsWithDefaultValues(fields, defaultValues);

  return formattedFields;
};

export default useOrphanConfigurationFields;
